import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Trash2, Check, FileText, Image, Upload, Download, Eye } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getPetNotes, addPetNote, deletePetNote, downloadFile } from '../services/api';
import { toast } from 'sonner';

export default function PetNotesModal({ petId, petName, onClose }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { loadNotes(); }, [petId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadNotes = async () => {
    try { const res = await getPetNotes(petId); setNotes(res.data); } catch (e) { console.error(e); }
  };

  const handleSubmit = async () => {
    if (!text.trim() && !file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('text', text.trim());
      if (file) fd.append('file', file);
      await addPetNote(petId, fd);
      setText(''); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      loadNotes();
      toast.success('Not eklendi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (noteId) => {
    try { await deletePetNote(petId, noteId); setDeletingId(null); loadNotes(); toast.success('Not silindi'); }
    catch (e) { toast.error('Silinemedi'); }
  };

  const openPreview = async (fileInfo) => {
    try {
      const res = await downloadFile(fileInfo.storage_path);
      const url = URL.createObjectURL(res.data);
      if (fileInfo.content_type === 'application/pdf') {
        window.open(url, '_blank');
      } else {
        setPreviewUrl(url);
      }
    } catch (e) { toast.error('Dosya açılamadı'); }
  };

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="notes-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-2xl w-full sm:max-w-md lg:max-w-3xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] shadow-2xl">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#6b6de3]/10 flex items-center justify-center">
              <FileText size={16} className="text-[#6b6de3]" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{petName} - Notlar</h2>
              <p className="text-xs text-[#8E8E93]">{notes.length} not</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" data-testid="close-notes-modal">
            <X size={20} className="text-[#8E8E93]" />
          </button>
        </div>

        {/* Add Note */}
        <div className="px-5 pt-4 pb-2 shrink-0 space-y-2">
          <div className="flex gap-2">
            <Input value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
              placeholder="Not ekle..." className="flex-1 h-10 bg-[#F2F2F7] border-0 rounded-xl text-sm" autoFocus data-testid="note-input" />
            <Button onClick={handleSubmit} disabled={saving || (!text.trim() && !file)}
              className="h-10 w-10 p-0 bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl disabled:opacity-40" data-testid="note-submit">
              <Send size={16} />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6b6de3] bg-[#6b6de3]/10 rounded-lg hover:bg-[#6b6de3]/20 transition-colors"
              data-testid="note-file-btn">
              <Upload size={12} /> {file ? file.name : 'Dosya Ekle'}
            </button>
            {file && <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }} className="text-[10px] text-[#FF3B30] hover:underline">Kaldır</button>}
            <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>

        {/* Notes List */}
        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {notes.length > 0 ? (
            <div className="space-y-2 pt-2">
              {notes.map(note => (
                <div key={note.id} className="p-3 bg-[#F2F2F7] rounded-xl group" data-testid={`note-${note.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      {note.text && <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{note.text}</p>}
                      {note.file && (
                        <button onClick={() => openPreview(note.file)}
                          className="mt-1.5 flex items-center gap-1.5 px-2.5 py-1.5 bg-white rounded-lg text-xs text-[#6b6de3] hover:bg-[#6b6de3]/10 transition-colors"
                          data-testid={`note-file-${note.id}`}>
                          {note.file.content_type?.startsWith('image') ? <Image size={12} /> : <FileText size={12} />}
                          <span className="truncate max-w-[180px]">{note.file.original_filename}</span>
                          <Eye size={12} />
                        </button>
                      )}
                      <p className="text-[10px] text-[#8E8E93] mt-1.5">
                        {note.created_by_name && <span className="text-[#6b6de3]">{note.created_by_name} · </span>}
                        {new Date(note.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {deletingId === note.id ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => handleDelete(note.id)} className="p-1.5 rounded-full bg-red-100 hover:bg-red-200"><Check size={14} className="text-[#FF3B30]" /></button>
                        <button onClick={() => setDeletingId(null)} className="p-1.5 rounded-full hover:bg-white"><X size={14} className="text-[#8E8E93]" /></button>
                      </div>
                    ) : (
                      <button onClick={() => setDeletingId(note.id)} className="p-1.5 rounded-full hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-testid={`delete-note-${note.id}`}>
                        <Trash2 size={14} className="text-[#FF3B30]" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText size={36} className="text-[#C7C7CC] mx-auto mb-3" />
              <p className="text-sm text-[#8E8E93]">Henüz not yok</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Overlay */}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <img src={previewUrl} alt="Önizleme" className="max-w-full max-h-full rounded-xl object-contain" />
        </div>
      )}
    </div>
  );
}
